<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    :max-width="maxWidth"
    :persistent="persistent"
    :scrollable="scrollable"
    transition="dialog-transition"
    class="base-modal"
    :z-index="zIndex"
  >
    <v-card
      :class="cardClass"
      :elevation="elevation"
      style="border-radius: 20px"
    >
      <!-- Header slot with close button -->
      <v-card-title
        v-if="showHeader"
        class="modal-header d-flex justify-space-between align-center"
        :class="headerClass"
      >
        <slot name="header" :close="closeModal">
          <span class="modal-title">{{ title }}</span>
        </slot>

        <v-btn
          v-if="showCloseButton"
          icon
          variant="text"
          size="small"
          @click="closeModal"
          class="modal-close-btn"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider v-if="showHeader && showDivider" />

      <!-- Main content -->
      <v-card-text :class="contentClass">
        <slot :close="closeModal" />
      </v-card-text>

      <!-- Footer slot -->
      <v-card-actions v-if="$slots.footer" :class="footerClass">
        <slot name="footer" :close="closeModal" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { defineProps, defineEmits } from "vue";

defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    default: "",
  },
  maxWidth: {
    type: [String, Number],
    default: 500,
  },
  persistent: {
    type: Boolean,
    default: false,
  },
  scrollable: {
    type: Boolean,
    default: true,
  },
  showHeader: {
    type: Boolean,
    default: true,
  },
  showCloseButton: {
    type: Boolean,
    default: true,
  },
  showDivider: {
    type: Boolean,
    default: true,
  },
  cardClass: {
    type: String,
    default: "",
  },
  headerClass: {
    type: String,
    default: "",
  },
  contentClass: {
    type: String,
    default: "pa-6",
  },
  footerClass: {
    type: String,
    default: "pa-4",
  },
  elevation: {
    type: Number,
    default: 8,
  },
  zIndex: {
    type: Number,
    default: 240,
  },
});

const emit = defineEmits(["update:modelValue", "close"]);

const closeModal = () => {
  emit("update:modelValue", false);
  emit("close");
};
</script>

<style scoped>
.base-modal :deep(.v-dialog) {
  border-radius: 16px;
}

.modal-header {
  padding: 10px 24px 10px 24px;
  border-bottom: none;
}

.modal-title {
  font-size: var(--fs-big);
  font-weight: 600;
  color: var(--primary-blue);
}

.modal-close-btn {
  color: #3b4648;
}

.modal-close-btn:hover {
  background-color: rgba(59, 70, 72, 0.1);
}

:deep(.v-card-text) {
  text-align: center;
}

/* Smooth transitions */
:deep(.dialog-transition-enter-active),
:deep(.dialog-transition-leave-active) {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

:deep(.dialog-transition-enter-from),
:deep(.dialog-transition-leave-to) {
  opacity: 0;
  transform: scale(0.9);
}

@media (max-width: 768px) {
  .modal-title {
    font-size: var(--fs-medium);
  }
}
</style>
